import Box from '@quintoandar/cozy-core/Box';
import Snackbar from '@quintoandar/cozy-core/Snackbar';
import { captureException } from '@quintoandar/fwp-error-monitoring';
import PropTypes from 'prop-types';
import React, { useReducer } from 'react';
import { useIntl } from 'react-intl';
import { DragAndDropFile } from './DragAndDropFile/DragAndDropFile.js';
import { S3uploader } from './S3uploader.js';
import { VimeoPoller } from './VimeoPoller.js';
import { SNACKBAR_ERRORS, UPLOAD_STATUS, VIDEO_UPLOADING_ACTIONS } from './constants';
import messages from './messages.js';
import { getVideoFileNameErrors, getVideoMetadataErrors } from './utils';
import { getVimeoUploaderReducer } from './vimeoUploaderReducer.js';

export const FileUploader = ({ houseId, onVideoLinkUpdate, shouldPoll }) => {
  const intl = useIntl();

  const getInitialState = () => ({
    file: null,
    snackBarError: null,
    currentUploadStatus: shouldPoll ? UPLOAD_STATUS.POLLING : UPLOAD_STATUS.UNSELECTED_FILE,
  });

  const [state, dispatch] = useReducer(getVimeoUploaderReducer(intl), getInitialState());

  const dispatchSnackBarMessage = (message) =>
    dispatch({ type: VIDEO_UPLOADING_ACTIONS.SET_SNACK_BAR_ERROR, payload: message });

  const onSnackBarError = error => {
    switch (error) {
      case SNACKBAR_ERRORS.FILE_NOT_VALID_ERROR:
        dispatchSnackBarMessage(intl.formatMessage(messages.notValidVideo));
        break;
      case SNACKBAR_ERRORS.UNSUPORTED_VIDEO_ERROR:
        dispatchSnackBarMessage(intl.formatMessage(messages.fileIsNotAVideo));
        break;
      case SNACKBAR_ERRORS.WRONG_VIDEO_POSITION_ERROR:
        dispatchSnackBarMessage(intl.formatMessage(messages.notInHorizontalError));
        break;
      case SNACKBAR_ERRORS.WRONG_VIDEO_WIDTH_ERROR:
        dispatchSnackBarMessage(intl.formatMessage(messages.minWidthError));
        break;
      default:
        dispatchSnackBarMessage(intl.formatMessage(messages.errorUploadingTryAgain));
        break;
    }
  };

  const handleFileDrop = files => {
    if (!files || !files.length) {
      dispatchSnackBarMessage(intl.formatMessage(messages.noFilesSelected));
      return;
    }
    const file = files[0];

    const fileNameError = getVideoFileNameErrors(file);
    if (fileNameError) {
      onSnackBarError(fileNameError);
      return;
    }

    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      const metadataError = getVideoMetadataErrors(video);
      if (metadataError) {
        onSnackBarError(metadataError);
        return;
      }
      dispatch({ type: VIDEO_UPLOADING_ACTIONS.SET_FILE, payload: file });
    };

    video.src = URL.createObjectURL(file);
  };

  const onUploadSuccess = () => {
    dispatch({ type: VIDEO_UPLOADING_ACTIONS.POLLING });
  };

  const onUploadError = (error) => {
    captureException(error?.error || error);
    const isAnotherVideoUploading = error?.isAxiosError && error?.response?.data?.code === 4008;
    if (isAnotherVideoUploading) {
      dispatch({ type: VIDEO_UPLOADING_ACTIONS.POLLING });
    } else {
      dispatch({ type: VIDEO_UPLOADING_ACTIONS.UPLOAD_ERROR });
    }
  };

  const onPollingError = (error) => {
    captureException(error?.error || error);
    dispatch({ type: VIDEO_UPLOADING_ACTIONS.POLLING_ERROR });
  };

  const onPollingSuccess = () => {
    dispatch({ type: VIDEO_UPLOADING_ACTIONS.POLLING_SUCCESS });
    onVideoLinkUpdate();
  };

  const isFileInputEnabled = state.currentUploadStatus === UPLOAD_STATUS.UNSELECTED_FILE ||
    state.currentUploadStatus === UPLOAD_STATUS.ERROR_POLLING ||
    state.currentUploadStatus === UPLOAD_STATUS.ERROR_UPLOADING;

  return (
    <Box>
      <DragAndDropFile
        title={intl.formatMessage(messages.moveVideoHere)}
        subtitle={intl.formatMessage(messages.videoSubtitle)}
        isFileInputEnabled={isFileInputEnabled}
        onFileDrop={handleFileDrop}
        inputType="video/*"
      />
      <Snackbar
        message={state.snackBarError}
        color="error"
        onClose={() => dispatch({ type: VIDEO_UPLOADING_ACTIONS.CLOSE_SNACK_BAR })}
        open={Boolean(state.snackBarError)}
      />
      <Box mt={3}>
        <S3uploader
          currentStatus={state.currentUploadStatus}
          file={state.file}
          onError={onUploadError}
          onUploadSuccess={onUploadSuccess}
          isUploadAlreadyDone={shouldPoll && !state.currentUploadStatus !== UPLOAD_STATUS.ERROR_POLLING}
          houseId={houseId}
        />
      </Box>
      <Box mt={2}>
        <VimeoPoller
          currentStatus={state.currentUploadStatus}
          houseId={houseId}
          onError={onPollingError}
          onSuccess={onPollingSuccess}
        />
      </Box>
    </Box>
  );
};

FileUploader.propTypes = {
  houseId: PropTypes.string,
  onVideoLinkUpdate: PropTypes.func.isRequired,
  shouldPoll: PropTypes.bool.isRequired,
};
